import { isObject } from '@mimir/primitive/type-guards/isObject';
import { invariant } from '@mimir/common/util/invariant';
import type { ArtifactReference } from '@mimir/primitive/artifactReference';
import type { ProjectItemInfo } from '@mimir/primitive/premiere/premiereInfo';
import type { CorePremiereClipInfo, PremiereClipInfo } from './premiereTypes';

export function projectItemArtifactReference(projectItemInfo: ProjectItemInfo): ArtifactReference | null {
  const artRefJson = projectItemInfo.meta['Mimir.Artifact'];
  if (artRefJson) {
    return interpretArtifactReference(artRefJson);
  }
  return null;
}

function interpretArtifactReference(artRefJson: string): ArtifactReference {
  const unk: unknown = JSON.parse(artRefJson);
  invariant(isObject(unk), 'Expected artifact reference to be object.');
  const partial: Partial<ArtifactReference> | null = unk;
  invariant(typeof partial.type === 'string', 'Expected artifact reference type identifier to be string.');

  const artRef = partial as ArtifactReference;
  // Since the JSON specs do not allow undefined values, JSON.parse() can never produce
  // an undefined value for the highResExt property of an artifact reference that had been
  // stringified before. Since the highResExt property is marked as mandatory, we need to
  // explicitly add the undefined property (otherwise deep equal comparisons will fail)
  if (artRef.type === 'TsSegmentRange' && artRef.highResExt === undefined) {
    artRef.highResExt = undefined;
  }
  return artRef;
}

/**
 * Any Adobe Premiere project item which does not have an explicit artifact
 * reference is interpreted as having an artifact reference of type
 * 'ImplicitReference'.
 */
export function getArtifactReference(clipInfo: CorePremiereClipInfo): ArtifactReference {
  return clipInfo.artifactRef ?? { type: 'ImplicitReference' };
}


/**
 * Returns the ID of the Mimir item associated to the given clip info
 * or null if there is no Mimir item.
 */
export function getItemIdFromPremiereClipInfo(clipInfo: PremiereClipInfo): string | null {
  const artifactReference = getArtifactReference(clipInfo);
  if (artifactReference.type !== 'ImplicitReference') {
    return artifactReference.itemId;
  }

  if (clipInfo.mimirItem !== undefined) {
    return clipInfo.mimirItem.id;
  }

  return null;
}
